import { useEffect, useRef, useState } from 'react';

/**
 * Mobile-friendly background audio hook.
 *
 * iOS Safari and Android Chrome block autoplay unless the audio is started
 * inside a synchronous user-gesture handler. This hook:
 *  1. Tries to autoplay at mount (works on desktop after first visit).
 *  2. Attaches `pointerdown` / `touchstart` / `click` / `keydown` listeners
 *     and starts audio from inside the gesture handler.
 *  3. Tracks `unlocked` state so the UI can show a small "Tap for sound"
 *     pill if the user wants to know the audio is waiting.
 *  4. Halves volume on small viewports (≤640px) so it isn't oppressive on
 *     mobile speakers.
 *
 * Returns `{ audio, unlocked, unlockNow }`.
 */
export function useUnlockableAudio(src, { loop = true, baseVolume = 1.0, startAt = 0, isMuted = false } = {}) {
  const audioRef = useRef(null);
  const [unlocked, setUnlocked] = useState(false);

  // Smaller volume on mobile so heartbeat / ambient isn't too aggressive.
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;
  const effectiveVolume = isMuted ? 0 : baseVolume * (isMobile ? 0.6 : 1.0);

  useEffect(() => {
    const audio = new Audio(src);
    audio.loop = loop;
    audio.preload = 'auto';
    audio.volume = 0;
    if (startAt) {
      audio.currentTime = startAt;
    }
    audioRef.current = audio;

    let alive = true;
    const markUnlocked = () => {
      if (alive) setUnlocked(true);
    };

    // Attempt 1: silent autoplay (most browsers allow muted/zero-volume autoplay).
    audio.play().then(markUnlocked).catch(() => { /* autoplay blocked — wait for gesture */ });

    // Attempt 2: synchronous gesture-driven unlock.
    const unlock = () => {
      try {
        const p = audio.play();
        if (p && typeof p.then === 'function') {
          p.then(markUnlocked).catch(() => { /* still blocked on this device — try next gesture */ });
        } else {
          markUnlocked();
        }
      } catch (unlockErr) {
        // Audio.play() can throw on very old Safari — non-fatal
        if (typeof window !== 'undefined' && window.console) {
          console.warn('Audio unlock attempt failed:', unlockErr);
        }
      }
    };
    document.addEventListener('pointerdown', unlock, { passive: true });
    document.addEventListener('touchstart', unlock, { passive: true });
    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);

    return () => {
      alive = false;
      audio.pause();
      audio.currentTime = 0;
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Volume / mute reactive update.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = effectiveVolume;
    }
  }, [effectiveVolume]);

  const unlockNow = () => {
    if (!audioRef.current) return;
    try {
      const p = audioRef.current.play();
      if (p && typeof p.then === 'function') {
        p.then(() => setUnlocked(true)).catch((playErr) => {
          console.warn('Audio play() rejected on manual unlock:', playErr);
        });
      }
    } catch (unlockNowErr) {
      console.warn('Audio unlock (manual) failed:', unlockNowErr);
    }
  };

  return { audio: audioRef.current, unlocked, unlockNow };
}

export default useUnlockableAudio;
